import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SortableTreeItem, { TreeItem } from "./SortableTreeItem";
import AddItemPanel from "./AddItemPanel";
import MenuPreview from "./MenuPreview";
import {
  INDENTATION_WIDTH,
  availableItemToFlatNode,
  MAX_LEVELS,
  buildTree,
  flattenTree,
  getChildCount,
  getProjection,
  getSubtreeHeight,
  isTreeDirty,
  removeChildrenOf,
} from "../utilities/menuTree";

const measuring = { droppable: { strategy: MeasuringStrategy.Always } };

export default function MenuTreeBuilder({
  menuData,
  initialList,
  availableItemsAll,
  saving,
  onSave,
  onDuplicate,
  onCreateItem,
  onEditItem,
  onDeleteAvailableItem,
  /* Task 24886 - a global menu is rolled out from ZCRM_Master and cannot be
     edited in a company. Rather than hide it (you would not know why your
     colleague's sidebar looks like that), the builder renders it whole and
     inert, with the one action that IS available: clone it. */
  readOnly = false,
  readOnlyNotice,
  duplicateLabel = "Duplicate",
}) {
  const [flatItems, setFlatItems] = useState([]);
  const [name, setName] = useState("");
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const savedFlatRef = useRef([]);

  // Reset working state whenever a different menu is selected or the saved
  // structure changes (e.g. after a successful save reloads it).
  const resetSignature = useMemo(
    () => `${menuData?.Id}:${JSON.stringify(initialList || [])}`,
    [menuData?.Id, initialList]
  );
  useEffect(() => {
    const flat = flattenTree(initialList || []);
    savedFlatRef.current = flat;
    setFlatItems(flat);
    setName(menuData?.Name || "");
    setCollapsedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignature]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const usedIds = useMemo(
    () => new Set(flatItems.map((i) => String(i.id))),
    [flatItems]
  );
  const availableUnused = useMemo(
    () => (availableItemsAll || []).filter((i) => !usedIds.has(String(i.Id))),
    [availableItemsAll, usedIds]
  );

  const flattenedItems = useMemo(() => {
    const collapsed = activeId
      ? [activeId, ...collapsedIds]
      : [...collapsedIds];
    return removeChildrenOf(flatItems, collapsed);
  }, [flatItems, collapsedIds, activeId]);

  const sortedIds = useMemo(
    () => flattenedItems.map((i) => i.id),
    [flattenedItems]
  );
  const activeItem = activeId
    ? flatItems.find((i) => i.id === activeId)
    : null;

  // Cap nesting so a dragged parent never pushes its own children past the
  // allowed depth.
  const activeMaxDepthCap = activeId
    ? Math.max(0, MAX_LEVELS - 1 - getSubtreeHeight(flatItems, activeId))
    : MAX_LEVELS - 1;
  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          INDENTATION_WIDTH,
          activeMaxDepthCap
        )
      : null;

  const previewTree = useMemo(() => buildTree(flatItems), [flatItems]);
  const dirty =
    isTreeDirty(flatItems, savedFlatRef.current) ||
    name.trim() !== (menuData?.Name || "");

  // ---- drag handlers ----
  function resetDrag() {
    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);
  }

  function handleDragStart({ active }) {
    setActiveId(active.id);
    setOverId(active.id);
  }
  function handleDragMove({ delta }) {
    setOffsetLeft(delta.x);
  }
  function handleDragOver({ over }) {
    setOverId(over?.id ?? null);
  }
  function handleDragEnd({ active, over }) {
    resetDrag();
    if (!projected || !over) return;

    const { depth, parentId } = projected;
    const cloned = flatItems.map((i) => ({ ...i }));
    const overIndex = cloned.findIndex((i) => i.id === over.id);
    const activeIndex = cloned.findIndex((i) => i.id === active.id);
    if (activeIndex === -1 || overIndex === -1) return;

    cloned[activeIndex] = { ...cloned[activeIndex], depth, parentId };
    const sorted = arrayMove(cloned, activeIndex, overIndex);
    // round-trip through buildTree to renormalise depth/parentId for descendants
    setFlatItems(flattenTree(buildTree(sorted)));
  }

  // ---- item handlers ----
  const handleAddItem = (item) => {
    setFlatItems((prev) => [...prev, availableItemToFlatNode(item)]);
  };

  const handleRemove = (id) => {
    const toRemove = new Set([id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const it of flatItems) {
        if (it.parentId && toRemove.has(it.parentId) && !toRemove.has(it.id)) {
          toRemove.add(it.id);
          changed = true;
        }
      }
    }
    setFlatItems(flatItems.filter((i) => !toRemove.has(i.id)));
  };

  const handleCollapse = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = () => onSave(buildTree(flatItems), name.trim());
  const handleReset = () => {
    setFlatItems(flattenTree(initialList || []));
    setName(menuData?.Name || "");
  };

  return (
    <Box>
      {/* header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          label="Menu name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          sx={{ minWidth: 260 }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {readOnly && readOnlyNotice ? (
            <Chip
              label={readOnlyNotice}
              color="info"
              size="small"
              variant="outlined"
            />
          ) : null}
          {!readOnly && dirty && (
            <Chip label="Unsaved changes" color="warning" size="small" variant="outlined" />
          )}
          <Button
            size="small"
            variant={readOnly ? "contained" : "text"}
            startIcon={<ContentCopyIcon />}
            onClick={() => onDuplicate(`${name.trim()} (copy)`, buildTree(flatItems))}
          >
            {duplicateLabel}
          </Button>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<UndoIcon />}
              onClick={handleReset}
              disabled={!dirty}
            >
              Undo
            </Button>
          )}
          {!readOnly && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save menu"}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {/* available items - nothing to add to a menu you cannot change */}
        {!readOnly && (
          <Grid item xs={12} md={5}>
            <AddItemPanel
              items={availableUnused}
              onAdd={handleAddItem}
              onCreate={onCreateItem}
              onEdit={onEditItem}
              onDelete={onDeleteAvailableItem}
            />
          </Grid>
        )}

        {/* tree + preview */}
        <Grid item xs={12} md={readOnly ? 12 : 7}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Menu structure
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.5,
              minHeight: 160,
              bgcolor: "action.hover",
            }}
          >
            {flatItems.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, color: "text.disabled" }}>
                {readOnly
                  ? "This global menu has no items yet."
                  : "Empty menu. Click items on the left to add them, then drag to nest and reorder."}
              </Typography>
            ) : readOnly ? (
              /* Same rows, no drag context and no remove handles: the shape is
                 the information, the handles would be a lie. */
              flattenedItems.map((item) => (
                <TreeItem
                  key={item.id}
                  item={item}
                  depth={item.depth}
                  childCount={getChildCount(flatItems, item.id)}
                />
              ))
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                measuring={measuring}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={resetDrag}
              >
                <SortableContext
                  items={sortedIds}
                  strategy={verticalListSortingStrategy}
                >
                  {flattenedItems.map((item) => {
                    const childCount = getChildCount(flatItems, item.id);
                    return (
                      <SortableTreeItem
                        key={item.id}
                        id={item.id}
                        item={item}
                        depth={
                          item.id === activeId && projected
                            ? projected.depth
                            : item.depth
                        }
                        childCount={childCount}
                        collapsed={collapsedIds.has(item.id) && childCount > 0}
                        onCollapse={
                          childCount > 0 ? () => handleCollapse(item.id) : undefined
                        }
                        onRemove={() => handleRemove(item.id)}
                        onEdit={
                          // Task 24886 - the Global Menus builder passes no
                          // editor (the catalogue is maintained elsewhere), so
                          // the handle has to be absent, not broken.
                          item.MenuNavItem_Id && onEditItem
                            ? () =>
                                onEditItem({
                                  Id: item.Id,
                                  Name: item.MenuNavItem_Name,
                                  Icon: item.MenuNavItem_Icon,
                                  Url: item.MenuNavItem_Url,
                                  MenuNavOption_Id: item.MenuNavItem_Id,
                                  FlagPlaceholder: 1,
                                })
                            : undefined
                        }
                      />
                    );
                  })}
                </SortableContext>

                <DragOverlay dropAnimation={null}>
                  {activeItem ? (
                    <TreeItem
                      clone
                      item={activeItem}
                      depth={0}
                      childCount={getChildCount(flatItems, activeItem.id) + 1}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <MenuPreview tree={previewTree} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
